import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

// Public pages live under /club and must read from the anonymous API mirror.
// Derived from the URL rather than passed down as a prop: these files have
// deeply nested components (drilldown sheets, modals, breakdown panels) that
// make their own requests, and threading a prop through every one of them is
// how you end up with `P is not defined` crashing a page on click.
function PUB() {
  return (typeof window !== 'undefined' && window.location.pathname.startsWith('/club'))
    ? '/public' : '';
}


const NAVY = '#1E3A6E';
const YELLOW = '#F5C518';
const GREEN = '#16a34a';
const RAJ = 'Rajdhani, sans-serif';

const thumb = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
const thumbHi = (id) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
const watchUrl = (v) => v.youtube_url || `https://www.youtube.com/watch?v=${v.video_id}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const fmtDay  = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';
const yearOf  = (d) => d ? new Date(d).getFullYear() : null;

// maxresdefault.jpg does not exist for every video — YouTube generates it some
// time after upload, and serves a grey 120x90 placeholder with HTTP **200**
// rather than a 404 when it is missing, so onError never fires. Measure the
// image instead: anything that small is the placeholder, so drop to hqdefault,
// which always exists.
function useThumb(videoId, preferHi) {
  const [src, setSrc] = useState(preferHi ? thumbHi(videoId) : thumb(videoId));
  return [
    src,
    (e) => { if (e.target.naturalWidth > 0 && e.target.naturalWidth <= 121) setSrc(thumb(videoId)); },
    () => setSrc(thumb(videoId)),
  ];
}

// Build a short result string from linked-match fields (or null if not a linked match).
function resultLine(v) {
  if (!v.winner_team) return null;
  const margin = (v.margin != null && v.margin_type) ? ` by ${v.margin} ${v.margin_type}` : '';
  return `${v.winner_team} won${margin}`;
}

// Inline styles cannot express media queries, and this page needs three
// genuinely responsive behaviours: a taller hero on a narrow screen, the play
// button lifted clear of the title, and a filter row that scrolls sideways
// instead of wrapping onto three lines.
const CSS = `
.vp-wrap        { max-width: 1100px; margin: 0 auto; padding: 20px 16px 64px; }
.vp-hero        { padding-top: 44%; }
.vp-hero-play   { top: 50%; }
.vp-hero-body   { padding: 22px 24px; }
.vp-hero-title  { font-size: clamp(1.15rem, 3vw, 1.7rem); max-width: 760px; }

/* One row on a wide screen, stacked on a phone. Every control is a fixed
   width or fluid — nothing here can grow with the number of seasons, which
   is what made the old chip row overflow the page sideways. */
.vp-filters     { display: flex; gap: 10px; align-items: center; margin-bottom: 22px; flex-wrap: wrap; }
.vp-select      { font-family: Rajdhani, sans-serif; font-weight: 700; font-size: 0.88rem;
                  padding: 8px 12px; border-radius: 10px; border: 1.5px solid #dbe2ee;
                  background: #fff; color: #1E3A6E; cursor: pointer; min-width: 150px; }
.vp-search      { flex: 1 1 180px; min-width: 0; padding: 9px 13px; border-radius: 10px;
                  border: 1.5px solid #dbe2ee; font-size: 0.88rem; outline: none; }
.vp-grid        { display: grid; grid-template-columns: repeat(auto-fill, minmax(248px, 1fr)); gap: 16px; }

@media (max-width: 640px) {
  .vp-hero       { padding-top: 62%; }
  .vp-hero-play  { top: 36%; }
  .vp-hero-body  { padding: 14px 16px 16px; }
  .vp-hero-title { font-size: 1.08rem; }
  .vp-filters    { gap: 8px; }
  .vp-select     { flex: 1 1 0; min-width: 0; }
  .vp-search     { flex: 1 1 100%; }
  .vp-grid       { grid-template-columns: 1fr; gap: 14px; }
}
`;

function PlayGlyph({ big }) {
  const w = big ? 76 : 50, h = big ? 52 : 35;
  return (
    <div style={{ width: w, height: h, background: 'rgba(12,20,38,0.78)',
      border: '1.5px solid rgba(255,255,255,0.35)', borderRadius: big ? 16 : 11,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 0, height: 0,
        borderTop: `${big ? 13 : 9}px solid transparent`,
        borderBottom: `${big ? 13 : 9}px solid transparent`,
        borderLeft: `${big ? 21 : 14}px solid #fff`, marginLeft: 4 }} />
    </div>
  );
}

// ─── Hero: the newest video ───────────────────────────────
function Hero({ v }) {
  const [src, onLoad, onError] = useThumb(v.video_id, true);
  const result = resultLine(v);
  return (
    <div style={{ borderRadius: 18, overflow: 'hidden', position: 'relative', marginBottom: 26,
      boxShadow: '0 14px 34px rgba(30,58,110,0.24)' }}>
      <div className="vp-hero" style={{ position: 'relative', background: '#0b1426' }}>
        <img src={src} onLoad={onLoad} onError={onError} alt=""
             style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(11,20,38,0.10) 0%, rgba(11,20,38,0.35) 52%, rgba(11,20,38,0.95) 100%)' }} />
        <a className="vp-hero-play" href={watchUrl(v)} target="_blank" rel="noopener noreferrer"
           aria-label={`Play ${v.title}`}
           style={{ position: 'absolute', left: '50%', transform: 'translate(-50%,-50%)' }}>
          <PlayGlyph big />
        </a>
        <div className="vp-hero-body" style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
          {result && (
            <div style={{ display: 'inline-block', background: GREEN, color: '#fff', fontFamily: RAJ,
              fontWeight: 800, fontSize: '0.72rem', padding: '4px 11px', borderRadius: 16, marginBottom: 9 }}>
              {result}
            </div>
          )}
          <a href={watchUrl(v)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <div className="vp-hero-title" style={{ fontFamily: RAJ, fontWeight: 800, color: '#fff',
              lineHeight: 1.16 }}>{v.title}</div>
          </a>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center',
            color: 'rgba(255,255,255,0.82)', fontSize: '0.82rem', marginTop: 7 }}>
            {v.video_date && <span>{fmtDate(v.video_date)}</span>}
            {(v.team_a_score || v.team_b_score) && (
              <span style={{ fontWeight: 600, color: '#fff' }}>{v.team_a_score} · {v.team_b_score}</span>
            )}
            {v.motm_name && <span style={{ color: YELLOW, fontWeight: 600 }}>⭐ {v.motm_name}</span>}
          </div>
          {v.match_id && (
            <Link to={`/scorecards/${v.match_id}`}
              style={{ display: 'inline-block', marginTop: 13, background: YELLOW, color: NAVY,
                fontFamily: RAJ, fontWeight: 800, fontSize: '0.8rem', padding: '8px 15px',
                borderRadius: 9, textDecoration: 'none' }}>
              View scorecard
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Grid card ────────────────────────────────────────────
function VideoCard({ v }) {
  const [hover, setHover] = useState(false);
  const [src, onLoad, onError] = useThumb(v.video_id, false);
  const result = resultLine(v);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
         style={{ borderRadius: 13, overflow: 'hidden', background: '#fff',
           border: `1px solid ${hover ? '#c9d4e8' : '#e6eaf2'}`,
           boxShadow: hover ? '0 10px 22px rgba(30,58,110,0.15)' : '0 1px 3px rgba(16,24,40,0.05)',
           transition: 'box-shadow .16s, border-color .16s',
           display: 'flex', flexDirection: 'column' }}>
      <a href={watchUrl(v)} target="_blank" rel="noopener noreferrer"
         style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        <div style={{ position: 'relative', paddingTop: '56.25%', background: '#0b1426' }}>
          <img src={src} onLoad={onLoad} onError={onError} alt=""
               style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0,
            background: hover ? 'rgba(11,20,38,0.22)' : 'rgba(11,20,38,0.04)', transition: 'background .16s' }} />
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
            <PlayGlyph />
          </div>
          {result && (
            <div style={{ position: 'absolute', top: 9, left: 9, background: GREEN, color: '#fff',
              fontFamily: RAJ, fontWeight: 800, fontSize: '0.66rem', padding: '3px 8px', borderRadius: 14 }}>
              {result}
            </div>
          )}
        </div>
      </a>
      <div style={{ padding: '11px 13px 13px', display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
        <a href={watchUrl(v)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <div style={{ fontFamily: RAJ, fontWeight: 700, color: NAVY, fontSize: '0.95rem', lineHeight: 1.24 }}>
            {v.title}
          </div>
        </a>
        <div style={{ color: '#64748b', fontSize: '0.74rem', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {v.video_date && <span>{fmtDay(v.video_date)}</span>}
          {(v.team_a_score || v.team_b_score) && (
            <span style={{ fontWeight: 600, color: '#475569' }}>{v.team_a_score} · {v.team_b_score}</span>
          )}
        </div>
        {v.motm_name && (
          <div style={{ fontSize: '0.74rem', color: '#b45309', fontWeight: 600 }}>⭐ {v.motm_name}</div>
        )}
        {v.match_id && (
          <Link to={`/scorecards/${v.match_id}`}
            style={{ marginTop: 'auto', paddingTop: 8, alignSelf: 'flex-start', fontFamily: RAJ,
              fontWeight: 700, fontSize: '0.75rem', color: NAVY, textDecoration: 'none',
              borderBottom: `2px solid ${YELLOW}`, paddingBottom: 1 }}>
            View scorecard
          </Link>
        )}
      </div>
    </div>
  );
}

export default function VideosPage({ publicMode = false }) {
  const [videos, setVideos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);

  // A cricket archive is browsed by season, so that is what the page is built
  // around — not the flat "Matches / Events" split it had, which told a reader
  // nothing once there were more than a handful of videos.
  const [season, setSeason]     = useState('all');
  const [category, setCategory] = useState('all');
  const [q, setQ]               = useState('');

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    api.get(`${PUB()}/videos`)
      .then(r => setVideos(r.data.videos || []))
      .catch(e => setErr(e?.response?.data?.error || 'Could not load the videos. Try again in a moment.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Seasons are derived from the videos, so the filter row never offers a year
  // with nothing behind it.
  const seasons = useMemo(() => {
    const counts = new Map();
    for (const v of videos) {
      const y = yearOf(v.video_date);
      if (y) counts.set(y, (counts.get(y) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[0] - a[0]);
  }, [videos]);

  const matchCount = videos.filter(v => v.category === 'match').length;
  const eventCount = videos.length - matchCount;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return videos.filter(v => {
      if (season !== 'all' && yearOf(v.video_date) !== Number(season)) return false;
      if (category !== 'all' && v.category !== category) return false;
      if (needle && !String(v.title || '').toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [videos, season, category, q]);

  // The hero only appears when nothing is filtered. Once someone is searching,
  // a hero pushes the results they asked for below the fold.
  const unfiltered = season === 'all' && category === 'all' && !q.trim();
  const featured   = unfiltered ? videos[0] : null;
  const listed     = featured ? filtered.filter(v => v.id !== featured.id) : filtered;

  const grouped = useMemo(() => {
    const byYear = new Map();
    for (const v of listed) {
      const y = yearOf(v.video_date) ?? 'Undated';
      if (!byYear.has(y)) byYear.set(y, []);
      byYear.get(y).push(v);
    }
    return [...byYear.entries()].sort((a, b) => {
      if (a[0] === 'Undated') return 1;
      if (b[0] === 'Undated') return -1;
      return b[0] - a[0];
    });
  }, [listed]);

  const oldest = seasons.length ? seasons[seasons.length - 1][0] : null;

  return (
    <div className="vp-wrap">
      <style>{CSS}</style>

      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: RAJ, fontWeight: 800, color: YELLOW, fontSize: '1.85rem', margin: 0 }}>
          🎥 Match Videos
        </h1>
        <div style={{ color: '#64748b', fontSize: '0.86rem', marginTop: 3 }}>
          {videos.length > 0 && oldest
            ? `${videos.length} replays going back to ${oldest}. New matches are added as they are uploaded.`
            : 'Full match replays from the club archive.'}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Loading…</div>
      ) : err ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>
          {err}
          <div style={{ marginTop: 12 }}>
            <button onClick={load} style={{ fontFamily: RAJ, fontWeight: 700, padding: '7px 15px',
              borderRadius: 8, border: `1.5px solid ${NAVY}`, background: '#fff', color: NAVY, cursor: 'pointer' }}>
              Try again
            </button>
          </div>
        </div>
      ) : videos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>
          No videos yet. Match replays will appear here as they are uploaded.
        </div>
      ) : (
        <>
          {featured && <Hero v={featured} />}

          {videos.length > 6 && (
            <div className="vp-filters">
              {/* Dropdowns, not chips. A chip per season was already too wide
                  at five seasons and pushed the page sideways on a phone; the
                  archive gains a season every year and still has 2013-2017 to
                  come, so the control must not grow with the data. */}
              <select className="vp-select" value={season} onChange={e => setSeason(e.target.value)}
                aria-label="Season">
                <option value="all">All seasons ({videos.length})</option>
                {seasons.map(([y, n]) => <option key={y} value={y}>{y} ({n})</option>)}
              </select>

              <select className="vp-select" value={category} onChange={e => setCategory(e.target.value)}
                aria-label="Type">
                <option value="all">Matches and events</option>
                {matchCount > 0 && <option value="match">Matches ({matchCount})</option>}
                {eventCount > 0 && <option value="event">Events ({eventCount})</option>}
              </select>

              <input className="vp-search" value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search titles" aria-label="Search titles" />
            </div>

          )}

          {listed.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 44, color: '#64748b' }}>
              Nothing matches that.
              <div style={{ marginTop: 10 }}>
                <button onClick={() => { setSeason('all'); setCategory('all'); setQ(''); }}
                  style={{ fontFamily: RAJ, fontWeight: 700, padding: '7px 15px', borderRadius: 8,
                    border: `1.5px solid ${NAVY}`, background: '#fff', color: NAVY, cursor: 'pointer' }}>
                  Clear filters
                </button>
              </div>
            </div>
          ) : (
            grouped.map(([year, vids]) => (
              <div key={year} style={{ marginBottom: 34 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 13,
                  borderBottom: '1px solid #e6eaf2', paddingBottom: 7 }}>
                  <span style={{ fontFamily: RAJ, fontWeight: 800, color: NAVY, fontSize: '1.3rem' }}>{year}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                    {vids.length} {vids.length === 1 ? 'video' : 'videos'}
                  </span>
                </div>
                <div className="vp-grid">
                  {vids.map(v => <VideoCard key={v.id} v={v} />)}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
